import { useCallback, useState } from 'react';
import { ChatAction, ChatUiContext } from '../../client/chatClient';
import { useChat } from '../../hooks/useChat';
import { useRegisterRobbieSend } from '../../contexts/RobbieUiContext';

/**
 * The wizard's one conversation with Robbie, held above both of his shapes
 * (the desktop rail and the phone sheet) so a step can talk to him too.
 *
 * `sendToRobbie` sends on the buyer's behalf — the message lands in the
 * thread like one they typed — and, when the phone sheet is the shape on
 * screen (`sheetActive`, the wizard's own `useIsMobile`), opens it so the
 * answer is visible. The desktop rail is always open, so a desktop send leaves
 * the sheet shut and a later resize to phone width does not pop it open.
 */

/**
 * Which tool results change data the wizard is showing. A BOM proposal
 * changes nothing until the buyer applies it (the card refreshes then), so it
 * must not trigger a re-read.
 */
export const actionsNeedRefresh = (actions: ChatAction[]): boolean =>
  actions.some((a) => a.uiEffect === 'RFQ_UPDATED' || a.uiEffect === 'RECOMMENDATION_UPDATED');

export const useWizardRobbie = (
  rfqId: string | undefined,
  step: number,
  onRefresh: () => void,
  sheetActive: boolean,
  /** The step-3 screen on view, so Robbie sees that screen's state and tools. */
  subStep?: number
) => {
  const [sheetOpen, setSheetOpen] = useState(false);

  const onActions = useCallback(
    (actions: ChatAction[]) => {
      if (actionsNeedRefresh(actions)) onRefresh();
    },
    [onRefresh]
  );

  const chat = useChat(rfqId, step, 'default', onActions, subStep);
  const { sendMessage } = chat;

  const sendToRobbie = useCallback(
    (text: string, uiContext?: ChatUiContext) => {
      if (sheetActive) setSheetOpen(true);
      sendMessage(text, uiContext);
    },
    [sendMessage, sheetActive]
  );
  useRegisterRobbieSend(sendToRobbie);

  return { chat, sendToRobbie, sheetOpen, setSheetOpen };
};

export default useWizardRobbie;
